import {computed, makeObservable, observable} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import {BaseModel} from '../../Base/Base.model';

const ModelName = 'ProgramDurationUnitModel';

export class ProgramDurationUnitModel extends BaseModel {
  constructor(public unit: ProgramDurationUnitModel.Unit) {
    super({
      id: '',
    });

    makeObservable<ProgramDurationUnitModel>(this, {
      max: computed,
      multiplier: computed,
      unit: observable,
    });
  }

  get max(): number {
    switch (this.unit) {
      case 'month':
        return 12;
      case 'week':
        return 52;
      default:
        return 365;
    }
  }

  static override get modelName(): typeof ModelName {
    return ModelName;
  }

  get modelName(): typeof ModelName {
    return ModelName;
  }

  get multiplier(): number {
    switch (this.unit) {
      case 'month':
        return ProgramDurationUnitModel.Duration.month;
      case 'week':
        return ProgramDurationUnitModel.Duration.week;
      default:
        return ProgramDurationUnitModel.Duration.day;
    }
  }

  static detect(duration: number): ProgramDurationUnitModel {
    if (duration % ProgramDurationUnitModel.Duration.month === 0) {
      return new ProgramDurationUnitModel('month');
    }

    if (duration % ProgramDurationUnitModel.Duration.week === 0) {
      return new ProgramDurationUnitModel('week');
    }

    return new ProgramDurationUnitModel('day');
  }

  format(
    num: number,
    {isSingular}: ProgramDurationUnitModel.FormatOptions = {isSingular: false},
  ): string {
    /**
     * @i18n_parser
     * t('shared:program.duration_unit.day', {count})
     * t('shared:program.duration_unit.month', {count})
     * t('shared:program.duration_unit.week', {count})
     */
    return i18n.t(`shared:program.duration_unit.${this.unit}`, {
      count: isSingular ? 1 : num,
    });
  }
}

export namespace ProgramDurationUnitModel {
  export type FormatOptions = {
    isSingular?: boolean;
  };

  export type Props = BaseModel.ModelToProps<ProgramDurationUnitModel>;

  export type Unit = 'day' | 'month' | 'week';

  export const Duration = {
    day: 1,
    month: 30,
    week: 7,
  };
}
